'use client';

import { useEffect, useState, useCallback, useRef } from 'react';

// ── TypeScript: BeforeInstallPromptEvent ──
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

/* ─────────────────────────────────────────────────────────
 * useIsStandalone
 * ─────────────────────────────────────────────────────────
 * Erkennt ob die App als installierte PWA (standalone) läuft.
 * 
 * Im standalone-Modus:
 * - Kein Browser-Chrome (Adressleiste, Tabs etc.)
 * - Eigenes App-Fenster
 * - Gelockerte Autoplay-Policies (Video mit Sound)
 * - App-Icon auf Startbildschirm/Desktop
 */
export function useIsStandalone(): boolean {
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const checkStandalone = () => {
      // W3C Standard
      const standalone = window.matchMedia('(display-mode: standalone)').matches;
      // iOS Safari
      const iosStandalone = (navigator as any).standalone === true;
      // TWA
      const twa = document.referrer.includes('android-app://');
      // Window Controls Overlay
      const wco = window.matchMedia('(display-mode: window-controls-overlay)').matches;

      setIsStandalone(standalone || iosStandalone || twa || wco);
    };

    checkStandalone();

    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    mediaQuery.addEventListener?.('change', checkStandalone);

    return () => {
      mediaQuery.removeEventListener?.('change', checkStandalone);
    };
  }, []);

  return isStandalone;
}

/**
 * Prüft ob die App als PWA installiert ist (nicht-React-Kontext).
 */
export function isPWAInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as any).standalone === true ||
    document.referrer.includes('android-app://')
  );
}

/* ─────────────────────────────────────────────────────────
 * useInstallPrompt
 * ─────────────────────────────────────────────────────────
 * Fängt den Browser-Event `beforeinstallprompt` ab und
 * stellt eine `promptInstall()`-Funktion bereit.
 *
 * Gibt zurück:
 * - canInstall: true wenn Installation möglich
 * - promptInstall: Funktion zum Auslösen des Dialogs
 * - isDismissed: true wenn User den Dialog zuvor abgelehnt hat
 */
const DISMISS_KEY = 'pwa-install-dismissed';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 Tage

export function useInstallPrompt() {
  const [canInstall, setCanInstall] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);
  const isStandalone = useIsStandalone();

  useEffect(() => {
    // Bereits installiert → nichts anzeigen
    if (isStandalone) {
      setCanInstall(false);
      return;
    }

    // Prüfe ob User den Dialog kürzlich abgelehnt hat
    try {
      const dismissedAt = localStorage.getItem(DISMISS_KEY);
      if (dismissedAt && Date.now() - parseInt(dismissedAt) < DISMISS_DURATION) {
        setIsDismissed(true);
        return;
      }
    } catch {}

    const handler = (e: BeforeInstallPromptEvent) => {
      // Standard-Dialog verhindern — wir zeigen unseren eigenen
      e.preventDefault();
      deferredPrompt.current = e;
      setCanInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Listener für erfolgreiche Installation
    window.addEventListener('appinstalled', () => {
      deferredPrompt.current = null;
      setCanInstall(false);
      try { localStorage.removeItem(DISMISS_KEY); } catch {}
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, [isStandalone]);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt.current) return false;

    try {
      await deferredPrompt.current.prompt();
      const { outcome } = await deferredPrompt.current.userChoice;
      deferredPrompt.current = null;

      if (outcome === 'accepted') {
        setCanInstall(false);
        return true;
      } else {
        // User hat abgelehnt — für 7 Tage merken
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
        setIsDismissed(true);
        setCanInstall(false);
        return false;
      }
    } catch {
      return false;
    }
  }, []);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setIsDismissed(true);
    setCanInstall(false);
  }, []);

  return { canInstall: canInstall && !isDismissed, promptInstall, dismiss, isDismissed };
}

/* ─────────────────────────────────────────────────────────
 * useSWUpdate
 * ─────────────────────────────────────────────────────────
 * Erkennt wenn ein neuer Service Worker verfügbar ist
 * (= neuer Build deployed). Gibt `updateAvailable` zurück
 * und eine `applyUpdate()` Funktion zum Neuladen.
 *
 * Prüft periodisch alle 60 Sekunden auf Updates und
 * reagiert sofort auf `controllerchange` Events.
 */
const SW_CHECK_INTERVAL = 60 * 1000; // 60 Sekunden

export function useSWUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const newSW = useRef<ServiceWorker | null>(null);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    if (process.env.NODE_ENV === 'development') return;

    let intervalId: ReturnType<typeof setInterval>;

    async function checkForUpdate() {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg) return;

        // Update-Check erzwingen
        await reg.update();
      } catch {
        // Netzwerkfehler — stiller Fehler
      }
    }

    async function init() {
      try {
        const reg = await navigator.serviceWorker.ready;

        // Wenn bereits ein wartender SW vorhanden ist
        if (reg.waiting) {
          newSW.current = reg.waiting;
          setUpdateAvailable(true);
        }

        // Auf neuen installierten SW warten
        reg.addEventListener('updatefound', () => {
          const installing = reg.installing;
          if (!installing) return;

          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              // Neuer SW installiert, alter noch aktiv → Update verfügbar!
              newSW.current = installing;
              setUpdateAvailable(true);
            }
          });
        });

        // Periodisch auf Updates prüfen
        intervalId = setInterval(checkForUpdate, SW_CHECK_INTERVAL);
      } catch {
        // SW nicht unterstützt
      }
    }

    // Sofort neuladen wenn ein neuer SW die Kontrolle übernimmt
    let refreshing = false;
    const onControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    init();

    return () => {
      clearInterval(intervalId);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  const applyUpdate = useCallback(() => {
    if (newSW.current) {
      // Sage dem wartenden SW er soll übernehmen
      newSW.current.postMessage({ type: 'SKIP_WAITING' });
    } else {
      // Fallback: Einfach neuladen
      window.location.reload();
    }
  }, []);

  return { updateAvailable, applyUpdate };
}
